import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Trash2, Upload, Check } from "lucide-react";
import { api, authHeaders, fmtError } from "@/lib/api";
import { uploadImage, PrimaryBtn } from "./ui";
import { DEFAULT_FIELD } from "@/components/editorial/theme";

const fieldItems = (content) => (content.field?.items?.length ? content.field.items : DEFAULT_FIELD);

function useTargets(content, projects, social) {
  return [
    { id: "portrait", label: "Your passport photo (intro)" },
    ...fieldItems(content).map((e, i) => ({ id: `field:${i}`, label: `Event pass photo · ${e.short || e.event}` })),
    ...projects.map((p) => ({ id: `project:${p.id}`, label: `Deck card · ${p.title}` })),
    ...social.map((s) => ({ id: `social:${s.id}`, label: `Reel thumbnail · ${s.client} ${s.series || ""}` })),
  ];
}

async function applyTo(target, url, { content, projects, social }) {
  const h = { headers: authHeaders() };
  if (target === "portrait") return axios.put(api("/api/admin/content/intro"), { value: { ...(content.intro || {}), portrait: url } }, h);
  const [kind, id] = target.split(":");
  if (kind === "field") {
    const items = fieldItems(content).map((e, i) => (i === Number(id) ? { ...e, image: url } : e));
    return axios.put(api("/api/admin/content/field"), { value: { ...(content.field || {}), items } }, h);
  }
  if (kind === "project") {
    const p = projects.find((x) => x.id === id);
    return axios.put(api(`/api/admin/projects/${id}`), { ...p, img: url }, h);
  }
  const s = social.find((x) => x.id === id);
  return axios.put(api(`/api/admin/social/${id}`), { ...s, img: url }, h);
}

function ImageCard({ f, targets, usedBy, onApply, onDelete }) {
  const [target, setTarget] = useState("");
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(f.url); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="border border-black/15 flex flex-col" data-testid={`image-card-${f.id}`}>
      <div className="aspect-[4/3] bg-black/5 overflow-hidden"><img src={api(f.url)} alt={f.filename} className="w-full h-full object-cover" /></div>
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <p className="text-xs font-bold truncate" title={f.filename}>{f.filename || "image"}</p>
        {usedBy.length > 0 && <p className="text-[11px] text-green-800">In use: {usedBy.join(", ")}</p>}
        <select data-testid={`image-target-${f.id}`} value={target} onChange={(e) => setTarget(e.target.value)} className="w-full border border-black/20 px-2 py-1.5 text-sm">
          <option value="">Use this image for…</option>
          {targets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <div className="flex gap-2 mt-auto pt-1">
          <PrimaryBtn disabled={!target} onClick={() => onApply(target, f.url)} data-testid={`image-apply-${f.id}`} className="flex-1 justify-center !px-2 !text-xs">Apply</PrimaryBtn>
          <button onClick={copy} title="Copy link" className="p-2 border border-black/20 rounded-full">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
          <button onClick={() => onDelete(f.id)} title="Remove from library" className="p-2 border border-black/20 rounded-full text-red-700" data-testid={`image-delete-${f.id}`}><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}

export function ImagesTab({ content, projects, social, reload }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const targets = useTargets(content, projects, social);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(api("/api/admin/files"), { headers: authHeaders() });
      setFiles(data);
    } catch (e) {
      toast.error(fmtError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setBusy(true);
    let ok = 0;
    const failed = [];
    for (const f of list) {
      try {
        await uploadImage(f);
        ok += 1;
      } catch (err) {
        failed.push(`${f.name}: ${fmtError(err)}`);
      }
    }
    if (ok) toast.success(`${ok} image${ok > 1 ? "s" : ""} uploaded`);
    failed.forEach((m) => toast.error(m));
    load();
    setBusy(false);
    e.target.value = "";
  };

  const onApply = async (target, url) => {
    try {
      await applyTo(target, url, { content, projects, social });
      toast.success("Image updated on the site");
      reload();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this image from the library?")) return;
    await axios.delete(api(`/api/admin/files/${id}`), { headers: authHeaders() });
    load();
  };

  const usedBy = (url) => [
    ...(content.intro?.portrait === url ? ["passport photo"] : []),
    ...fieldItems(content).filter((e) => e.image === url).map((e) => `pass ${e.short}`),
    ...projects.filter((p) => p.img === url).map((p) => p.title),
    ...social.filter((s) => s.img === url).map((s) => `${s.client} ${s.series || ""}`),
  ];

  return (
    <div data-testid="tab-images">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-black">Images</h2>
          <p className="text-sm text-black/60">Upload pictures, then choose where each one goes: your passport photo, an event pass, a deck card or a reel thumbnail.</p>
        </div>
        <label className="inline-flex items-center gap-2 px-4 py-2 text-white font-bold text-sm uppercase tracking-wider rounded-full cursor-pointer" style={{ background: "#4A0E17" }} data-testid="images-upload">
          <Upload size={14} /> {busy ? "Uploading…" : "Upload images"}
          <input type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={onUpload} disabled={busy} data-testid="images-upload-input" />
        </label>
      </div>
      {files.length === 0 && <p className="text-sm text-black/50">No images yet. Upload one to get started.</p>}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {files.map((f) => <ImageCard key={f.id} f={f} targets={targets} usedBy={usedBy(f.url)} onApply={onApply} onDelete={onDelete} />)}
      </div>
    </div>
  );
}
